import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"

export default class extends Controller {
  static targets = ["contactStatus", "memo", "lockInfo"]
  static values = { entryId: Number, locked: Boolean }

  connect() {
    this.isEditing = false
    this.debounceTimer = null
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  updateField() {
    this.saveEntry()
  }

  openEdit(event) {
    // NOTE: ロック中は button.disabled で click 自体が発火しないため lockedValue チェックは不要。
    // 以前は lockedValue を見ていたが、自分自身のロック状態を巻き込んで再 open できない不具合があった。
    const btn = event.currentTarget
    const channelController = this.findChannelController()
    if (!channelController) return
    channelController.lockEntryVisual(this.entryIdValue)
    channelController.openEdit(
      this.entryIdValue,
      btn.dataset.entryCandidateName,
      btn.dataset.entryContactStatus,
      btn.dataset.entryMemo,
      btn.dataset.entryConfirmedSchedule,
      btn.dataset.entryConfirmedScheduleEnd,
      btn.dataset.entryTestSchedule,
      btn.dataset.entryVenueId,
      btn.dataset.entryMeetingUrl,
      btn.dataset.entryMeetingId,
      btn.dataset.entryMeetingPasscode
    )
  }

  updateFieldAndUnlock() {
    this.saveEntry()
    this.isEditing = false
    const channelController = this.findChannelController()
    if (channelController) {
      channelController.unlockEntry(this.entryIdValue)
    }
  }

  debouncedUpdate() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    this.debounceTimer = setTimeout(() => {
      this.saveEntry()
    }, 500)
  }

  lockRow() {
    if (this.lockedValue) return

    // NOTE: 同一行内のフィールド間移動で pending な unlock をキャンセル
    if (this.unlockTimer) {
      clearTimeout(this.unlockTimer)
      this.unlockTimer = null
    }

    this.isEditing = true
    const channelController = this.findChannelController()
    if (channelController) {
      channelController.lockEntry(this.entryIdValue)
    }
  }

  unlockRow() {
    // NOTE: 同一行内のフィールド間移動で不要な unlock を防ぐため遅延
    this.unlockTimer = setTimeout(() => {
      this.unlockTimer = null
      this.isEditing = false
      const channelController = this.findChannelController()
      if (channelController) {
        channelController.unlockEntry(this.entryIdValue)
      }
    }, 300)
  }

  async saveEntry() {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const url = this.buildEntryUrl()

    const body = {
      entry: {
        contact_status: this.hasContactStatusTarget ? this.contactStatusTarget.value : null,
        memo: this.hasMemoTarget ? this.memoTarget.value : null
      }
    }

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify(body)
      })

      if (!response.ok && response.status === 423) {
        this.showLockWarning()
      } else if (!response.ok && response.status === 422) {
        const data = await response.json().catch(() => null)
        showToast(data?.errors?.join(" / ") || "保存できませんでした")
      } else if (!response.ok) {
        showToast("保存できませんでした。時間をおいて もう一度お試しください")
      } else {
        await this.applyResponsePayload(response)
      }
    } catch (error) {
      console.error("Failed to save entry:", error)
      showToast("通信できませんでした。接続を確認して もう一度お試しください")
    }
  }

  async applyResponsePayload(response) {
    if (!response.ok) return
    const contentType = response.headers.get("content-type") || ""
    if (!contentType.includes("application/json")) return
    const data = await response.json()
    if (!data || !data.type || data.type === "noop") return
    const channelEl = this.element.closest("[data-controller~='workspace-channel']")
    if (!channelEl) return
    const channelController = this.application.getControllerForElementAndIdentifier(channelEl, "workspace-channel")
    if (channelController) channelController.handleMessage(data)
  }

  async remove() {
    if (!confirm("この候補者をリストから削除しますか？")) return

    // NOTE: 即座にフェードアウトして応答感を出す
    this.element.style.opacity = "0.3"
    this.element.style.pointerEvents = "none"

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const url = this.buildEntryUrl()

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken }
      })

      if (!response.ok) {
        this.element.style.opacity = "1"
        this.element.style.pointerEvents = ""
      } else {
        // NOTE: broadcast 到達を待たずレスポンスから DOM を更新する
        await this.applyResponsePayload(response)
      }
    } catch (error) {
      this.element.style.opacity = "1"
      this.element.style.pointerEvents = ""
      console.error("Failed to remove entry:", error)
    }
  }

  setLockState(locked, lockerName) {
    this.lockedValue = locked

    // 編集・削除ボタンの活性切替
    const buttons = this.element.querySelectorAll("button[data-action]")
    buttons.forEach(btn => {
      btn.disabled = locked
      btn.classList.toggle("opacity-40", locked)
      btn.classList.toggle("cursor-not-allowed", locked)
    })

    if (locked) {
      // インラインスタイルで一時的に背景色を上書き（元の優先順位 bg を保持）
      this.element.style.backgroundColor = "rgb(243 244 246)" // gray-100
      this.element.classList.add("opacity-60")
      if (this.hasLockInfoTarget) {
        this.lockInfoTarget.innerHTML = `
          <span class="inline-flex items-center gap-1 text-amber-600">
            <svg class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd"/>
            </svg>
            ${this.escapeHtml(lockerName)}が編集中
          </span>`
      }
    } else {
      // インラインスタイルをクリアして元のクラスベース bg を復元
      this.element.style.backgroundColor = ""
      this.element.classList.remove("opacity-60")
      if (this.hasLockInfoTarget) {
        this.lockInfoTarget.innerHTML = ""
      }
    }
  }

  releaseLockIfUser(_userId) {
    if (this.lockedValue) {
      this.setLockState(false, null)
    }
  }

  // NOTE: 他の人が編集中で保存が弾かれたとき。 黙って捨てると 入力が保存されたと誤解される。
  showLockWarning() {
    showToast("この候補者は他のユーザーが編集中です")
  }

  findChannelController() {
    const channelEl = this.element.closest("[data-controller~='workspace-channel']")
    if (!channelEl) return null
    return this.application.getControllerForElementAndIdentifier(channelEl, "workspace-channel")
  }

  buildEntryUrl() {
    const path = window.location.pathname
    const match = path.match(/\/admin\/projects\/(\d+)\/selection_lists\/(\d+)/)
    if (!match) return ""
    return `/admin/projects/${match[1]}/selection_lists/${match[2]}/entries/${this.entryIdValue}`
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
